import { parse as dateFnsParse } from 'date-fns';
import { type FormikTouched } from 'formik';
import * as Yup from 'yup';

import {
  DATE_FORMATS,
  EXPRESSIONS_TYPES,
  FIELD_TYPES,
  SENSITIVE_FIELD_MASK,
  VALIDATION_TYPES,
} from '../../../shared/constants/global';
import {
  type FieldElement,
  type FormValues,
  type ValidationRule,
} from '../../../shared/types';
import { isField, isRadioField, isRow, isSection } from '../../../shared/utils';
import {
  convertToPickerDateFormat,
  validateManualDateInput,
} from '../../../shared/utils/dateFormatUtils';
import { type EntityInitialData } from '../data-store';

import {
  createFieldValidator,
  type FieldSchema,
  isFieldConditionallyRequired,
  type LocalizeFn,
} from './utils';

const parseBool = (v: unknown): boolean => {
  if (typeof v === 'boolean') {
    return v;
  }

  if (typeof v === 'number') {
    return v === 1;
  }

  if (typeof v === 'string') {
    const s = v.trim().toLowerCase();

    return s === 'true' || s === '1';
  }

  return false;
};

export const getRequiredValidation = (
  field: FieldElement
): ValidationRule | undefined => {
  if ('validations' in field && Array.isArray(field.validations)) {
    return field.validations.find(
      (rule) => rule.type === VALIDATION_TYPES.REQUIRED
    );
  }

  return undefined;
};

/**
 * DA forms only: when the REQUIRED expression block is enabled, static
 * VALIDATION_TYPES.REQUIRED must not add Yup requiredness (expression path only).
 * If the expression is enabled but misconfigured, isFieldConditionallyRequired may be
 * false while static is suppressed — operators should complete REQUIRED/NOT_REQUIRED arms.
 */
const daFormShouldSuppressStaticRequired = (field: FieldElement): boolean => {
  if (!('expressions' in field) || !Array.isArray(field.expressions)) {
    return false;
  }

  const requiredExpression = field.expressions.find(
    (expr) => expr.type === EXPRESSIONS_TYPES.REQUIRED
  );

  return Boolean(requiredExpression?.enabled);
};

const generateValidationShapeWithMaps = (
  fields: FieldElement[],
  currentValues: FormValues,
  touched: FormikTouched<FormValues>,
  visibilityMaps: {
    fieldVisibility: Map<string, boolean>;
    sectionVisibility: Map<string, boolean>;
    fieldToSection: Map<string, string>;
  },
  entityData: EntityInitialData | null,
  l: LocalizeFn,
  isDaForm = false
): Record<string, FieldSchema> => {
  let shape: Record<string, FieldSchema> = {};

  if (!fields) {
    return shape;
  }

  fields.forEach((field) => {
    if (isField(field)) {
      // Check visibility
      const fieldVisible = visibilityMaps.fieldVisibility.get(field.id) ?? true;
      const sectionId = visibilityMaps.fieldToSection.get(field.id);
      const sectionVisible = sectionId
        ? (visibilityMaps.sectionVisibility.get(sectionId) ?? true)
        : true;

      if (!fieldVisible || !sectionVisible) {
        return;
      }

      // Determine if field is required
      const requiredValidation = getRequiredValidation(field);
      const effectiveRequiredValidation =
        isDaForm && daFormShouldSuppressStaticRequired(field)
          ? undefined
          : requiredValidation;
      const isStaticRequired =
        Boolean(effectiveRequiredValidation) && !field.isReadOnly;
      const isConditionallyRequired = isFieldConditionallyRequired(
        field,
        currentValues,
        touched,
        entityData,
        isDaForm
      );
      const isRequired = isStaticRequired || isConditionallyRequired;

      // In update mode, sensitive/password fields with the backend sentinel
      // already have a server-side value — skip required validation until
      // the user activates the field (which clears the sentinel).
      const isSensitiveWithExistingValue =
        (field.fieldType === FIELD_TYPES.PASSWORD ||
          field.fieldType === FIELD_TYPES.SENSITIVE) &&
        currentValues[field.fieldName] === SENSITIVE_FIELD_MASK;

      // Create validator using factory
      const validator = createFieldValidator({
        field,
        l,
        isRequired: isSensitiveWithExistingValue ? false : isRequired,
        requiredMessage: requiredValidation?.message,
        currentValues,
        touched,
        entityData,
      });

      if (validator) {
        shape[field.fieldName] = validator;
      }
    }

    // Recurse into sections/rows
    if (
      (isSection(field) || isRow(field)) &&
      field.children &&
      field.children.length > 0
    ) {
      shape = {
        ...shape,
        ...generateValidationShapeWithMaps(
          field.children,
          currentValues,
          touched,
          visibilityMaps,
          entityData,
          l,
          isDaForm
        ),
      };
    }
  });

  return shape;
};

export const generateDynamicValidationSchemaWithMaps = (
  fields: FieldElement[],
  currentValues: FormValues,
  touched: FormikTouched<FormValues>,
  visibilityMaps: {
    fieldVisibility: Map<string, boolean>;
    sectionVisibility: Map<string, boolean>;
    fieldToSection: Map<string, string>;
  },
  entityData: EntityInitialData | null,
  l: LocalizeFn,
  isDaForm = false
): Yup.ObjectSchema<Record<string, unknown>> => {
  const shape = generateValidationShapeWithMaps(
    fields,
    currentValues,
    touched,
    visibilityMaps,
    entityData,
    l,
    isDaForm
  );

  return Yup.object().shape(shape);
};

const parseCommaSeparated = (raw: string): string[] =>
  raw
    .split(',')
    .map((s) => s.trim())
    .filter((s) => s.length > 0);

export const buildInitialValues = (
  elements: FieldElement[],
  acc: FormValues = {},
  prefilledData: Record<string, unknown> | null = null
): FormValues => {
  if (!elements) {
    return acc;
  }

  elements.forEach((field) => {
    if (isField(field)) {
      // Check if prefilledData has a value for this field
      const hasPrefilledValue =
        prefilledData && field.fieldName in prefilledData;
      const prefilledValue = hasPrefilledValue
        ? prefilledData[field.fieldName]
        : undefined;

      if (field.fieldType === FIELD_TYPES.SINGLESELECT) {
        if (hasPrefilledValue) {
          acc[field.fieldName] = prefilledValue as FormValues[string];
        } else {
          (acc as Record<string, unknown>)[field.fieldName] = undefined;
        }
      } else if (field.fieldType === FIELD_TYPES.MULTISELECT) {
        acc[field.fieldName] = hasPrefilledValue
          ? (prefilledValue as FormValues[string])
          : [];
      } else if (field.fieldType === FIELD_TYPES.CHECKBOX) {
        if (hasPrefilledValue) {
          acc[field.fieldName] = prefilledValue as FormValues[string];
        } else {
          const raw = field.defaultValue?.value;

          acc[field.fieldName] = raw ? parseCommaSeparated(raw) : [];
        }
      } else if (isRadioField(field)) {
        if (hasPrefilledValue) {
          acc[field.fieldName] = prefilledValue as FormValues[string];
        } else {
          acc[field.fieldName] = field.defaultValue?.value?.trim() || null;
        }
      } else if (field.fieldType === FIELD_TYPES.DATE) {
        if (hasPrefilledValue) {
          (acc as Record<string, unknown>)[field.fieldName] = prefilledValue;
        } else {
          const defaultValue = field.defaultValue?.value;

          if (defaultValue) {
            const advConfig =
              'advConfig' in field
                ? (field.advConfig as { dateFormat?: string } | undefined)
                : undefined;
            const pickerFormat = convertToPickerDateFormat(
              advConfig?.dateFormat ?? ''
            );
            const isValid = validateManualDateInput(defaultValue, pickerFormat);
            let parsed: Date | null = isValid
              ? dateFnsParse(defaultValue, pickerFormat, new Date())
              : null;

            // Fallback: if the default value doesn't match the configured format,
            // try all known date formats (handles DA forms where the backend may
            // provide values in a different format than the field's configured format)
            if (!parsed) {
              const allPickerFormats = Object.values(DATE_FORMATS).map(
                (fmt) => fmt.picker
              );

              parsed = allPickerFormats.reduce<Date | null>(
                (found, fallbackFormat) => {
                  if (found || fallbackFormat === pickerFormat) {
                    return found;
                  }

                  if (!validateManualDateInput(defaultValue, fallbackFormat)) {
                    return null;
                  }

                  return dateFnsParse(defaultValue, fallbackFormat, new Date());
                },
                null
              );
            }

            (acc as Record<string, unknown>)[field.fieldName] = parsed;
          } else {
            (acc as Record<string, unknown>)[field.fieldName] = null;
          }
        }
      } else if (field.fieldType === FIELD_TYPES.TEXT) {
        acc[field.fieldName] = hasPrefilledValue
          ? (prefilledValue as FormValues[string])
          : field.defaultValue?.value || '';
      } else if (field.fieldType === FIELD_TYPES.NUMBER) {
        if (hasPrefilledValue) {
          acc[field.fieldName] = prefilledValue as FormValues[string];
        } else {
          const defaultVal = field.defaultValue?.value;

          acc[field.fieldName] = defaultVal ? Number(defaultVal) : null;
        }
      } else if (field.fieldType === FIELD_TYPES.BOOLEAN) {
        if (hasPrefilledValue) {
          acc[field.fieldName] = prefilledValue as FormValues[string];
        } else {
          const isRequired = getRequiredValidation(field) !== undefined;

          // Required boolean fields start as null so the validator can distinguish
          // "never interacted" (null) from "explicitly set to false" (false).
          // Non-required fields use the configured default value (or false).
          if (isRequired) {
            acc[field.fieldName] = null;
          } else {
            const boolField = field as { defaultValue?: { value?: unknown } };
            const defaultVal = boolField.defaultValue?.value;

            acc[field.fieldName] = parseBool(defaultVal);
          }
        }
      } else if (field.fieldType === FIELD_TYPES.TERMSANDCONDITIONS) {
        acc[field.fieldName] = hasPrefilledValue
          ? (prefilledValue as FormValues[string])
          : false;
      } else {
        acc[field.fieldName] = hasPrefilledValue
          ? (prefilledValue as FormValues[string])
          : field.defaultValue?.value || '';
      }
    }

    if ((isSection(field) || isRow(field)) && field.children) {
      buildInitialValues(field.children, acc, prefilledData);
    }
  });

  return acc;
};
